import { defineComponent as _defineComponent } from 'vue'
import { Offer } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import PromotionRowNew from '@/components/buybox-new/PromotionRowNew.vue';
import { computed, ref } from 'vue';
import { useUserAgent } from '@/helpers/composables/useUserAgent';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import BuyBoxFreeTrialSwiperOSContainer from './BuyBoxFreeTrialSwiperOSContainer.vue';
import { freeTrialSwiperRules, SwiperRule } from '@/helpers/promotion-helper';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { UsePromotion } from '@/helpers/composables/usePromotion';
import { OfferLocation } from '@/helpers/offer-helper';
import {
	geoExperimentSwiperRules,
	geoSwiperCountriesOrder,
	GeoSwiperExpVariants,
} from '@/components/experiments/GeoSwiperExp';
import { fauxExperimentSwiperRules, useFauxSwiperExp } from '@/components/experiments/FauxSwiperExp';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';

interface ClickoutPayload {
	buyboxOffer: UsePromotion['promotionOffer'];
	offerLocation: OfferLocation.FREE_TRIAL;
	row: HTMLElement | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxFreeTrialSwiperOS',
  props: {
    offers: null,
    title: null,
    geoSwiperExpVariant: null,
    geoLocationCountry: null,
    fauxSwiperExpHasAnimated: { type: Boolean }
  },
  emits: ["clickOut", "fauxExpAnimationed"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'clickOut', payload: ClickoutPayload): void;
	// FAUX_SWIPER_EXP
	(emit: 'fauxExpAnimationed'): void;
	// FAUX_SWIPER_EXP
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	offers: Offer[];
	title: TitleDetail;
	geoSwiperExpVariant?: GeoSwiperExpVariants | null;
	geoLocationCountry?: string | null;
	// FAUX_SWIPER_EXP
	fauxSwiperExpHasAnimated?: boolean;
	// FAUX_SWIPER_EXP
};





const { userBrowser } = useUserAgent();
const { country } = useLanguageStore();

// FAUX_SWIPER_EXP
const { fauxSwiperExpVariant, trackFauxSwiperExp } = useFauxSwiperExp();
// FAUX_SWIPER_EXP

const freeTrialSwiperActive = computed(() => {
	if (props.geoSwiperExpVariant) {
		return geoExperimentSwiperRules[props.geoSwiperExpVariant || ''] != null;
	}
	if (fauxSwiperExpVariant.value) {
		return fauxExperimentSwiperRules[fauxSwiperExpVariant.value] != null;
	}
	return freeTrialSwiperRules[country.value] != null;
});

const freeTrialSwiperConfig = computed<SwiperRule | null>(() => {
	if (props.geoSwiperExpVariant) {
		return geoExperimentSwiperRules[props.geoSwiperExpVariant ?? ''] ?? null;
	}
	// FAUX_SWIPER_EXP
	if (fauxSwiperExpVariant.value) {
		return fauxExperimentSwiperRules[fauxSwiperExpVariant.value] ?? null;
	}
	// FAUX_SWIPER_EXP
	return freeTrialSwiperRules[country.value] ?? null;
});

const swiperPackages = computed(() => freeTrialSwiperConfig.value?.packages ?? null);

const swipeIndex = ref(0);

const isShowOrSeason = computed(() => ['Show', 'Season'].includes(props.title.__typename));

const slideDelay = computed(() => freeTrialSwiperConfig.value?.slideDelay ?? 1500);

const swiperPackageId = computed(() => {
	if (freeTrialSwiperConfig.value == null) return null;

	const browserRule = freeTrialSwiperConfig.value.rules.find(rule =>
		rule.selectors.browsers.includes(userBrowser.value)
	);
	if (browserRule) return browserRule.package;

	return freeTrialSwiperConfig.value.default.package;
});

const swiperSlideToIndex = computed(() => {
	if (props.geoSwiperExpVariant === 'variant_2') {
		return Math.max(0, geoSwiperCountriesOrder.indexOf(props.geoLocationCountry ?? ''));
	}
	if (props.geoSwiperExpVariant === 'variant_1') {
		return Math.max(0, geoSwiperCountriesOrder.slice(0, 2).indexOf(props.geoLocationCountry ?? ''));
	}
	// FAUX_SWIPER_EXP
	if (fauxSwiperExpVariant.value) {
		return fauxSwiperExpVariant.value === 'variant_1' ? 1 : 0;
	}
	// FAUX_SWIPER_EXP
	if (swiperPackages.value == null || swiperPackageId.value == null) return 0;

	return Math.max(0, swiperPackages.value.indexOf(swiperPackageId.value));
});

// FAUX_SWIPER_EXP
const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	once: true,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

function onVisibilityChanged(isVisible: boolean) {
	if (isVisible && fauxSwiperExpVariant.value) trackFauxSwiperExp({ action: 'impression' });
}
// FAUX_SWIPER_EXP

function getHardcodedFallbackOfferId(packageId: number, index: number) {
	if (props.geoSwiperExpVariant === 'variant_2' && getGeoLocationCountry(index) === 'CA') {
		return 119;
	}

	return packageId;
}

function getGeoLocationCountry(index: number) {
	if (!props.geoSwiperExpVariant || index === 0) {
		return undefined;
	}

	return props.geoSwiperExpVariant === 'variant_2' ? geoSwiperCountriesOrder[index] : 'GB';
}

function slideChange({
	direction,
	position,
	skipTracking,
	nonInteraction,
}: {
	direction: string;
	position: number;
	skipTracking: boolean;
	nonInteraction: boolean;
}) {
	swipeIndex.value = position;

	if (!skipTracking) {
		TrackingHelper.trackEvent('userinteraction', {
			action: 'swipe_clicked',
			label: direction,
			nonInteraction,
		});
	}

	// FAUX_SWIPER_EXP
	if (fauxSwiperExpVariant.value && !nonInteraction && !skipTracking) {
		trackFauxSwiperExp({
			action: 'click',
			label: `swiper_${direction}`,
		});
	}
	// FAUX_SWIPER_EXP
}

return { __sfc: true,props, emit, userBrowser, country, fauxSwiperExpVariant, trackFauxSwiperExp, freeTrialSwiperActive, freeTrialSwiperConfig, swiperPackages, swipeIndex, isShowOrSeason, slideDelay, swiperPackageId, swiperSlideToIndex, observeVisibilityOptions, onVisibilityChanged, getHardcodedFallbackOfferId, getGeoLocationCountry, slideChange, PromotionRowNew, BuyBoxFreeTrialSwiperOSContainer }
}

})